import dayjs from 'dayjs';
import { PVResultDto, Optimizer } from '../pv-calculator.dto';
import { downloadFileFromString } from 'src/common/downloadFile';
import i18n from 'src/infrastructure/i18n/i18next.client';
import { translateProfitLossGroup, translateProfitLossItemName } from './profitLossLabels';

interface ExportData {
    optimizedResult: PVResultDto;
    fullRoofResult: PVResultDto;
    pvAnalysisName: string;
    variantName: string;
    optimizer: Optimizer;
    fundingRate: number;
}

/**
 * Exports PV analysis results to CSV format, localized to the app's selected language.
 * Includes profit/loss accumulated data along with design, yield, economical and ecological information
 */
export function exportPvAnalysisToCSV(data: ExportData): void {
    const { optimizedResult, fullRoofResult, pvAnalysisName, variantName } = data;
    const t = i18n.t;

    const timestamp = dayjs().format('YYYY-MM-DD_HH.MM');
    const filename = `PA_Export_${pvAnalysisName.replace(/[\\/:"*?<>|]/g, '_')}_${variantName.replace(/[\\/:"*?<>|]/g, '_')}_${timestamp}.csv`;

    let csvContent = '';

    csvContent += `${escapeCsvValue(t('Potential Analysis Export'))}\n`;
    csvContent += `${escapeCsvValue(t('Analysis Name'))},${escapeCsvValue(pvAnalysisName)}\n`;
    csvContent += `${escapeCsvValue(t('Variant Name'))},${escapeCsvValue(variantName)}\n`;
    csvContent += `${escapeCsvValue(t('Export Date'))},${escapeCsvValue(new Date().toLocaleString('de-DE'))}\n`;
    csvContent += `\n`;

    csvContent += exportResultToCSV(t('Optimized Result'), optimizedResult, data.fundingRate);
    csvContent += `\n`;
    csvContent += exportResultToCSV(t('Full Roof Result'), fullRoofResult, 0);

    downloadFileFromString(csvContent, filename, 'text/csv;charset=utf-8;');
}

function exportResultToCSV(resultName: string, result: PVResultDto, fundingRate: number): string {
    const t = i18n.t;

    let csv = `${escapeCsvValue(resultName)}\n`;
    csv += `\n`;

    csv += `${escapeCsvValue(t('Design and Yield Information'))}\n`;
    csv += row(t('Metric'), t('Value'), t('Unit'));
    csv += row(t('PV System Power (kWp)'), formatNumber(result.design_kwp), 'kWp');
    csv += row(t('Annual Yield'), formatNumber(result.design_kwh_per_year_ac), 'kWh/a');
    csv += row(
        t('Specific Yield'),
        formatNumber(result.design_kwp > 0 ? result.design_kwh_per_year_ac / result.design_kwp : 0),
        'kWh/kWp',
    );
    csv += row(t('Battery Capacity'), formatNumber(result.battery_capacity), 'kWh');
    if (result.panel_count !== undefined) {
        csv += row(t('Panel Count'), `${result.panel_count}`, t('pieces'));
    }
    csv += `\n`;

    csv += `${escapeCsvValue(t('Economical Information'))}\n`;
    csv += row(t('Metric'), t('Value'), t('Unit'));
    csv += row(t('Investment Costs'), formatNumber(result.initial_investment), 'EUR');
    if (fundingRate > 0) {
        csv += row(t('Own Share (with funding)'), formatNumber(result.initial_investment_incl_funding), 'EUR');
        csv += row(t('Funding Rate'), formatNumber(fundingRate), t('percent'));
    }
    csv += row(t('Total Savings (30 years)'), formatNumber(result.balance_30_years), 'EUR');
    csv += row(t('Rate of Return'), formatNumber(result.rate_of_return * 100), t('percent'));
    csv += row(
        t('Amortization Period'),
        result.amortization_years !== null ? `${result.amortization_years}` : t('n/a'),
        t('years'),
    );
    csv += row(t('Self-Consumption Rate'), formatNumber(result.ratio_own_use * 100), t('percent'));
    if (result.self_sufficiency_rate !== undefined) {
        csv += row(t('Self-Sufficiency Rate'), formatNumber(result.self_sufficiency_rate * 100), t('percent'));
    }
    csv += `\n`;

    csv += `${escapeCsvValue(t('Ecological Information'))}\n`;
    csv += row(t('Metric'), t('Value'), t('Unit'));
    csv += row(t('CO2 Savings (annual)'), formatNumber(result.co2_savings), t('tons'));
    csv += row(t('Equivalent Trees'), formatNumber(result.trees), t('trees'));
    csv += `\n`;

    csv += `${escapeCsvValue(t('Profit/Loss Accumulated (Year by Year)'))}\n`;
    csv += row(t('Year'), t('Cumulative Profit/Loss'), t('Unit'));
    result.profit_loss_accumulated.forEach((value, index) => {
        csv += row(`${index}`, formatNumber(value), 'EUR');
    });
    csv += `\n`;

    if (result.profit_loss_statement && result.profit_loss_statement.length > 0) {
        csv += `${escapeCsvValue(t('Profit/loss breakdown'))}\n`;
        csv += row(t('Type'), t('Position'), t('Amount'), t('Year'), t('Inflation Rate'), t('Lifetime'), t('Unit'));

        result.profit_loss_statement.forEach((item) => {
            const year = 'year_index' in item ? `${item.year_index}` : '';
            const inflationRate = 'inflation_rate' in item ? formatNumber(item.inflation_rate * 100) : '';
            const lifetime = 'lifetime' in item ? `${item.lifetime}` : '';

            csv += row(
                translateProfitLossGroup(item.type, t),
                translateProfitLossItemName(item.name, t),
                formatNumber(item.amount),
                year,
                inflationRate,
                lifetime,
                'EUR',
            );
        });
        csv += `\n`;
    }

    if (result.load_summary && result.load_summary.length > 0) {
        csv += `${escapeCsvValue(t('Energy Consumption Components'))}\n`;
        csv += row(t('Component'), t('Annual Usage'), t('Unit'));
        result.load_summary.forEach(([name, value]) => {
            csv += row(name, formatNumber(value), 'kWh/a');
        });
        csv += `\n`;
    }

    return csv;
}

function row(...cells: string[]): string {
    return `${cells.map(escapeCsvValue).join(',')}\n`;
}

function formatNumber(value: number): string {
    return value.toFixed(2).replace('.', ',');
}

function escapeCsvValue(value: string): string {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}
